import React, { useState } from "react";
import { api, BACKEND_URL, formatApiError, openApiFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

const TIPOS = [
  { key: "productos", label: "Productos comerciales", descr: "Código interno, nombre, registro sanitario, costo, margen, precio. Requiere al menos un producto base creado." },
  { key: "clientes", label: "Clientes", descr: "RUC/cédula, razón social, dirección, crédito. Se aprueban automáticamente." },
  { key: "lotes", label: "Lotes", descr: "Vincula lote a producto (por código interno) y bodega (por nombre). Fechas en formato YYYY-MM-DD." },
];

export default function Imports() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});

  const descargarPlantilla = (tipo) => {
    openApiFile(`/imports/plantilla/${tipo}`, {
      filename: `plantilla-${tipo}.xlsx`,
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      downloadOnly: true,
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const onFile = async (tipo, file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) return toast.error("Solo archivos .xlsx");
    setLoading(l => ({ ...l, [tipo]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/imports/upload/${tipo}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(r => ({ ...r, [tipo]: data }));
      toast.success(`${tipo}: ${data.creados} creados, ${data.actualizados} actualizados${data.errores_total ? `, ${data.errores_total} errores` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(l => ({ ...l, [tipo]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="imports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Importación masiva Excel</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Cargue productos, clientes o lotes desde archivos .xlsx. Descargue la plantilla oficial para asegurar el formato correcto.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {TIPOS.map(t => {
          const r = results[t.key];
          return (
            <Card key={t.key} className="p-5 border border-[#d1dbd6] flex flex-col" data-testid={`import-card-${t.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="text-[#205c45]" size={20} />
                <h3 className="font-semibold text-[#15241d]">{t.label}</h3>
              </div>
              <p className="text-xs text-[#5c7368] flex-1 mb-4">{t.descr}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => descargarPlantilla(t.key)} data-testid={`template-${t.key}`}>
                  <Download size={14} className="mr-1" />Plantilla
                </Button>
                <label className="flex-1">
                  <Button variant="default" size="sm" asChild disabled={loading[t.key]} className="bg-[#205c45] w-full">
                    <span className="cursor-pointer">
                      <Upload size={14} className="mr-1" />
                      {loading[t.key] ? "Cargando..." : "Subir Excel"}
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => onFile(t.key, e.target.files[0])}
                    data-testid={`upload-${t.key}`}
                  />
                </label>
              </div>
              {r && (
                <div className="mt-4 rounded-md bg-[#e9f2ee] border border-[#b2d0c2] p-3 text-xs">
                  <div className="flex items-center gap-2 font-semibold text-[#205c45]">
                    <CheckCircle2 size={14} />
                    Resultado
                  </div>
                  <div className="mt-2 space-y-1 text-[#15241d]">
                    <div>Total filas: <strong>{r.total_filas}</strong></div>
                    <div>Creados: <strong className="text-[#1f6b42]">{r.creados}</strong></div>
                    <div>Actualizados: <strong className="text-[#1f6b42]">{r.actualizados}</strong></div>
                    {r.errores_total > 0 && (
                      <div className="text-[#7a2514] flex items-start gap-1 mt-2">
                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                        <div>
                          <strong>{r.errores_total} errores:</strong>
                          <ul className="mt-1 list-disc pl-4">
                            {r.errores?.slice(0, 5).map((e, i) => <li key={i}>fila {e.fila}: {e.error}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-5 border border-[#d1dbd6] bg-[#fef7ec]">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-[#8a5500] shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-[#5c3d00]">
            <strong>Consejos:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
              <li>La primera fila debe contener las cabeceras exactas (use la plantilla).</li>
              <li>Si un código interno/RUC ya existe, el registro se <em>actualiza</em>.</li>
              <li>Los errores se reportan por fila sin abortar la importación completa.</li>
              <li>Para Lotes: el producto debe existir y la bodega debe coincidir por nombre.</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
